import type { Strapi5Error } from '@nuxtjs/strapi';
import { useQueryClient } from '@tanstack/vue-query';
import { getDynamicPage } from '~/api/callbacks/pages';
import type { DynamicPageData } from '~/types/pages/dynamicPageData';
import type { Locale } from '~/types';

export default defineNuxtRouteMiddleware(async (to) => {
  const nuxtApp = useNuxtApp();

  // Skips middleware on initial client load
  if (
    import.meta.client &&
    nuxtApp.isHydrating &&
    nuxtApp.payload.serverRendered
  )
    return;

  try {
    const data = await fetchDynamicPage(to.path, nuxtApp.$i18n.locale);

    useUpdatePageMeta(data.seo, to.path);
    updateTitle(data.seo?.title);
    await updateGlobalState(data);
  } catch (error) {
    const strapiError = (error as Strapi5Error)?.error;

    if (
      strapiError?.status === 400 &&
      strapiError?.message === 'Unsupported locale'
    ) {
      return navigateTo(to.fullPath.replace('/en/', '/ru/'));
    }

    showError({
      statusMessage: strapiError?.message || 'Internal server error',
      statusCode: strapiError?.status || 500,
      fatal: true,
    });
  }
});

function fetchDynamicPage(path: string, locale: Ref<string>) {
  const { device } = useGetDevice();
  const pagePath = ref(cleanPagePath(path));
  const queryClient = useQueryClient();

  return queryClient.fetchQuery({
    queryKey: ['dynamicPage', pagePath, locale],
    queryFn: () => {
      return getDynamicPage({
        path: pagePath.value,
        locale: locale.value as Locale,
        device: device.value,
      });
    },
  });
}

async function updateGlobalState(data: DynamicPageData) {
  const treeNavigation = data.navigation.tree;
  const flatNavigation = data.navigation.flat;
  const navigationItem = data.navigationItem;
  const blocks = data.page?.blocks;
  const headerColor = data.page?.headerColor;

  const pageStore = usePageStore();

  // Ensures that the stale page data is reset after navigating to a new page
  pageStore.treeNavigation = null;
  pageStore.flatNavigation = null;
  pageStore.navigationItem = null;
  pageStore.blocks = [];
  pageStore.headerColor = 'dark';
  pageStore.showENLocale = false;
  pageStore.headerType = 'default';
  await nextTick();

  pageStore.treeNavigation = treeNavigation || null;
  pageStore.flatNavigation = flatNavigation || null;
  pageStore.navigationItem = navigationItem || null;
  pageStore.blocks = blocks || [];
  pageStore.headerColor = headerColor || 'dark';
  pageStore.showENLocale = data.showENLocale;
  pageStore.headerType = data.headerType;
}

function updateTitle(title: string | undefined | null) {
  if (title) {
    useHead({
      title,
    });
  }
}
